import { Controller } from "@hotwired/stimulus"
import { countMessage, isShortcut, keyAction, moveIndex, options, rows, searchable, suggestUrl } from "../lib/global_search_logic"

// The global search box (app/views/shared/_search_box, docs/crm/search.md):
// a form to /search that works without this controller. With it, typing asks
// GET /search/suggest?q= (debounced) and shows the top hits of each group in
// a listbox under the field; ↓/↑ move through them, Enter opens the
// highlighted one (or submits the form when none is), Escape closes the
// list and then leaves the field. "/" anywhere outside a field focuses the
// box that has the shortcut.
const DEBOUNCE = 150
const BLUR_DELAY = 150

export default class extends Controller {
  static targets = ["input", "listbox", "status"]
  static values = { url: String, shortcut: Boolean }

  connect() {
    this.cache = new Map()
    this.items = []
    this.active = -1
    this.closeList()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.blurTimer)
    this.request?.abort()
  }

  // --- events ------------------------------------------------------------

  shortcut(event) {
    if (!this.shortcutValue) return
    const target = event.target || {}
    if (!isShortcut({ key: event.key, ctrlKey: event.ctrlKey, metaKey: event.metaKey, altKey: event.altKey,
                      tagName: target.tagName, isContentEditable: target.isContentEditable })) return
    event.preventDefault()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  search() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(), DEBOUNCE)
  }

  open() {
    clearTimeout(this.blurTimer)
    if (this.items.length > 0 && this.loadedQuery === this.inputTarget.value.trim()) this.showList()
  }

  close() {
    this.blurTimer = setTimeout(() => this.closeList(), BLUR_DELAY)
  }

  // A click on the list must not blur the field before it opens a hit.
  keepFocus(event) {
    event.preventDefault()
  }

  pick(event) {
    const item = event.target.closest("[role=option]")
    if (!item) return
    event.preventDefault()
    this.visit(item.dataset.url)
  }

  async keydown(event) {
    const action = keyAction(event.key, { open: this.isOpen, active: this.active, count: this.items.length })
    if (!action) return
    if (action !== "dismiss") event.preventDefault()

    switch (action) {
      case "open": this.showList(); this.highlight(moveIndex(-1, event.key === "ArrowUp" ? -1 : 1, this.items.length)); break
      case "next": this.highlight(moveIndex(this.active, 1, this.items.length)); break
      case "prev": this.highlight(moveIndex(this.active, -1, this.items.length)); break
      case "go": this.visit(this.items[this.active].url); break
      case "close": case "dismiss": this.closeList(); break
      case "blur": this.inputTarget.blur(); break
    }
  }

  // --- suggestions ---------------------------------------------------------

  async load() {
    clearTimeout(this.timer)
    const query = this.inputTarget.value.trim()
    if (!searchable(query)) {
      this.items = []
      this.closeList()
      return
    }
    try {
      const answer = this.cache.has(query) ? this.cache.get(query) : await this.fetchAnswer(query)
      this.cache.set(query, answer)
      if (query !== this.inputTarget.value.trim()) return
      this.loadedQuery = query
      this.render(rows(answer))
    } catch (error) {
      if (error.name === "AbortError") return
      this.items = []
      this.closeList()
      this.announce("Vyhľadávanie sa nepodarilo")
    }
  }

  async fetchAnswer(query) {
    this.request?.abort()
    this.request = new AbortController()
    const response = await fetch(suggestUrl(this.urlValue, query), {
      headers: { Accept: "application/json" }, credentials: "same-origin", signal: this.request.signal
    })
    if (!response.ok) throw new Error(`search answered ${response.status}`)
    return response.json()
  }

  render(list) {
    this.items = options(list)
    this.active = -1
    this.listboxTarget.replaceChildren(...list.map((row) => (row.heading ? this.headingElement(row) : this.optionElement(row))))
    this.showList()
    this.announce(countMessage(this.items.filter((item) => item.group !== "all").length))
  }

  headingElement(row) {
    const item = document.createElement("li")
    item.setAttribute("role", "presentation")
    item.className = "flex items-baseline justify-between px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase"
    item.textContent = row.label
    return item
  }

  optionElement(row) {
    const item = document.createElement("li")
    item.id = `${this.listboxTarget.id}_${row.index}`
    item.setAttribute("role", "option")
    item.setAttribute("aria-selected", "false")
    item.dataset.url = row.url
    item.dataset.group = row.group
    item.className = "flex min-h-9 cursor-pointer flex-col rounded-lg px-2 py-1.5 text-sm text-ink hover:bg-canvas aria-selected:bg-brand-tint aria-selected:text-brand"
    const title = document.createElement("span")
    title.className = row.group === "all" ? "font-medium text-brand" : "font-medium"
    title.textContent = row.title
    item.append(title)
    if (row.detail) {
      const detail = document.createElement("span")
      detail.className = "text-xs text-ink-muted"
      detail.textContent = row.detail
      item.append(detail)
    }
    return item
  }

  highlight(index) {
    this.active = index
    this.listboxTarget.querySelectorAll("[role=option]").forEach((item, position) => {
      item.setAttribute("aria-selected", position === index ? "true" : "false")
      if (position === index) item.scrollIntoView({ block: "nearest" })
    })
    if (index >= 0) {
      this.inputTarget.setAttribute("aria-activedescendant", `${this.listboxTarget.id}_${index}`)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  showList() {
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  closeList() {
    this.listboxTarget.hidden = true
    this.active = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  get isOpen() {
    return !this.listboxTarget.hidden
  }

  visit(url) {
    if (!url) return
    this.closeList()
    if (window.Turbo) window.Turbo.visit(url)
    else window.location.assign(url)
  }

  announce(message) {
    this.statusTarget.textContent = message
  }
}
